import React, { useState } from 'react';
import AppointmentDetails from '../../components/Appointment/AppointmentDetails';
import MonthlyCalendar from '../../components/MonthlyCalendar';
import UserCreationForm from '../../components/UserCreationForm';
import { useAuth } from '../../utils/auth';

const StaffDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showUserCreationForm, setShowUserCreationForm] = useState(false);
  const [userCreationSuccess, setUserCreationSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // eslint-disable-next-line
  const [staffId, setStaffId] = useState<string>('1'); // Mock staff ID

  // Mock data for dashboard
  const stats = {
    todayAppointments: 12,
    pendingDonations: 8,
    completedToday: 15
  };

  const upcomingAppointments = [
    { id: 1, name: 'John Doe', time: '10:00 AM', bloodType: 'A+', status: 'confirmed' },
    { id: 2, name: 'Jane Smith', time: '11:30 AM', bloodType: 'O-', status: 'confirmed' },
    { id: 3, name: 'Robert Johnson', time: '1:15 PM', bloodType: 'B+', status: 'pending' },
    { id: 4, name: 'Emily Davis', time: '2:45 PM', bloodType: 'AB+', status: 'confirmed' },
    { id: 5, name: 'Michael Wilson', time: '4:00 PM', bloodType: 'O+', status: 'pending' }
  ];

  const handleUserCreationSuccess = () => {
    setUserCreationSuccess(true);
    setTimeout(() => {
      setUserCreationSuccess(false);
      setShowUserCreationForm(false);
    }, 2000);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="md:flex md:items-center md:justify-between mb-8">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Staff Dashboard
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Welcome back, {user?.name}. Here's your schedule for today.
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                  type="button"
                  onClick={() => setShowUserCreationForm(true)}
                  className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Create New User
              </button>
            </div>
          </div>

          {/* User Creation Form Modal */}
          {showUserCreationForm && (
              <div className="fixed z-10 inset-0 overflow-y-auto">
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                  </div>

                  <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                  <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                          {userCreationSuccess ? (
                              <div className="flex flex-col items-center justify-center py-6">
                                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                  <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <h3 className="mt-2 text-lg font-medium text-gray-900">User Created Successfully!</h3>
                              </div>
                          ) : (
                              <>
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                  Create New User
                                </h3>
                                <div className="mt-2">
                                  <p className="text-sm text-gray-500">
                                    As a staff member, you can create new users with member or staff roles.
                                  </p>
                                </div>
                                <div className="mt-4">
                                  <UserCreationForm onSuccess={handleUserCreationSuccess} />
                                </div>
                              </>
                          )}
                        </div>
                      </div>
                    </div>
                    {!userCreationSuccess && (
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                          <button
                              type="button"
                              onClick={() => setShowUserCreationForm(false)}
                              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                    )}
                  </div>
                </div>
              </div>
          )}

          {/* Dashboard Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                  onClick={() => setActiveTab('overview')}
                  className={`${
                      activeTab === 'overview'
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Overview
              </button>
              <button
                  onClick={() => setActiveTab('appointments')}
                  className={`${
                      activeTab === 'appointments'
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Appointments
              </button>
              <button
                  onClick={() => setActiveTab('donations')}
                  className={`${
                      activeTab === 'donations'
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Donations Date
              </button>
              <button
                  onClick={() => setActiveTab('inventory')}
                  className={`${
                      activeTab === 'inventory'
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Inventory
              </button>
            </nav>
          </div>

          {/* Dashboard Content */}
          <div className="mt-6">
            {activeTab === 'overview' && (
                <div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {/* Card 1 */}
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Today's Appointments
                              </dt>
                              <dd>
                                <div className="text-lg font-medium text-gray-900">
                                  {stats.todayAppointments}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 2 */}
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Pending Donations
                              </dt>
                              <dd>
                                <div className="text-lg font-medium text-gray-900">
                                  {stats.pendingDonations}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 3 */}
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Completed Today
                              </dt>
                              <dd>
                                <div className="text-lg font-medium text-gray-900">
                                  {stats.completedToday}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Calendar and Appointments */}
                  <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Monthly Schedule
                      </h3>
                      <MonthlyCalendar staffId={staffId} onDateSelect={handleDateSelect} />
                    </div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Appointments for {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </h3>
                      <AppointmentDetails staffId={staffId} date={selectedDate} />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Upcoming Appointments
                    </h3>
                    <div className="mt-4 flex flex-col">
                      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Donor
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Time
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Blood Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                  <span className="sr-only">Actions</span>
                                </th>
                              </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                              {upcomingAppointments.map((appointment) => (
                                  <tr key={appointment.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                            <span className="text-red-600 font-medium">{appointment.name.charAt(0)}</span>
                                          </div>
                                        </div>
                                        <div className="ml-4">
                                          <div className="text-sm font-medium text-gray-900">{appointment.name}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{appointment.time}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{appointment.bloodType}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      appointment.status === 'confirmed'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {appointment.status}
                                  </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <a href="/checkin" className="text-red-600 hover:text-red-900 mr-4">Check In</a>
                                      <a href="/reschedule" className="text-gray-600 hover:text-gray-900">Reschedule</a>
                                    </td>
                                  </tr>
                              ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {activeTab === 'appointments' && (
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    All Appointments
                  </h3>
                  <AppointmentDetails />
                </div>
            )}

            {activeTab === 'donations' && (
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Donations Details
                  </h3>
                  {/* <DonationProcessPage /> */}
                  
                </div>
            )}

          </div>
        </div>
      </div>
  );
};

export default StaffDashboard;